/**
 * @module lib/wagmi
 * @category Configuration
 * @description wagmi (viem) configuration for the marketplace hooks.
 *
 * The wagmi hooks in `src/hooks/` (useAuctionManager, useConfigurator,
 * useDeployer, useNFT) need a wagmi `WagmiProvider` with its own chain list
 * and transports. This config is built from `src/chainData.json` so the wagmi
 * layer and the AppKit/ethers5 layer always agree on RPCs and network names.
 *
 * AppKit (src/App.tsx) remains the wallet UI and connection source of truth
 * for the existing containers; wagmi's `injected()` connector is provided so
 * the new hooks can be used immediately. A later migration (see TODO.md,
 * "Migrate project to wagmi") is meant to make wagmi the single wallet layer
 * and retire AppKit.
 */

import { http, createConfig, fallback } from 'wagmi';
import { injected, walletConnect } from 'wagmi/connectors';
import type { Chain } from 'viem';
import { chainData } from '../chains';

const ZERO = '0x0000000000000000000000000000000000000000';

/** Build a viem Chain from a chainData.json row. */
function toChain(row: any): Chain {
    return {
        id: parseInt(row.chainId, 10),
        name: row.chainName,
        nativeCurrency: {
            name: row.tokName,
            symbol: row.tokName,
            decimals: 18,
        },
        rpcUrls: {
            default: { http: [row.rpc] },
        },
        blockExplorers: {
            default: {
                name: `${row.chainName} Explorer`,
                url: row.exp || row.explorer || '',
            },
        },
        testnet: !!row.hidden || [16, 114, 31337, 10143, 143].includes(parseInt(row.chainId, 10)),
    } as Chain;
}

// Only rows that carry an RPC and a real AuctionManager contract: the hooks
// only make sense where a marketplace is deployed.
const usable = chainData.filter(
    (row: any) => row.rpc && row.chainId && row.aManager && row.aManager !== ZERO
);

export const wagmiChains = usable.map(toChain) as [Chain, ...Chain[]];

/** Lookup of chainId -> chainData row, for the marketplace hook consumers. */
export const chainDataById = new Map<number, any>(
    chainData.filter((row: any) => row.chainId).map((row: any) => [parseInt(row.chainId, 10), row])
);

/**
 * AuctionManager (Diamond proxy) address per chainId, taken from chainData.json.
 * This is the same source the rest of the marketplace uses.
 */
export const auctionManagerByChain: Record<number, string> = Object.fromEntries(
    usable.map((row: any) => [parseInt(row.chainId, 10), row.aManager])
);

const transports = Object.fromEntries(
    wagmiChains.map((chain) => {
        const row = chainDataById.get(chain.id);
        // A chain whose explorer/RPC is unreliable gets a fallback transport so a
        // single flaky provider does not break every hook call.
        return [
            chain.id,
            row && row.exp
                ? fallback([http(row.rpc), http(row.rpc)], { rank: false })
                : http(row!.rpc),
        ];
    })
);

const connectors = [injected()];

// WalletConnect is only wired up when a project id is available; the marketplace
// already runs WalletConnect through AppKit with its own project id.
const wcProjectId = import.meta.env?.VITE_WALLETCONNECT_PROJECT_ID as string | undefined;
if (wcProjectId) {
    connectors.push(
        walletConnect({ projectId: wcProjectId, showQrModal: false })
    );
}

export const wagmiConfig = createConfig({
    chains: wagmiChains,
    connectors,
    transports,
    ssr: true,
});

declare module 'wagmi' {
    interface Register {
        config: typeof wagmiConfig;
    }
}
